import axiosInstance from "./axiosInstance.ts";
import logger from "../utils/logger.ts";

/**
 * Knowledge base admin API.
 *
 * Backend module: optima-backend/src/modules/knowledge/knowledge-admin.controller.ts
 * All endpoints require the `recruitment-manage-knowledge` (or `admin`) role on
 * the Keycloak `recruitment-app` client.
 */

export type KnowledgeSourceType = 'FILE' | 'PASTE' | 'URL';
export type KnowledgeIndexStatus = 'PENDING' | 'INDEXING' | 'READY' | 'FAILED';
export type KnowledgeAudience = 'WORKER' | 'ADMIN' | 'BOTH';

export interface KnowledgeScope {
    profession?: string[];
    language?: string[];
    audience: KnowledgeAudience;
    tags?: string[];
}

export interface KnowledgeEntrySummary {
    id: string;
    title: string;
    sourceType: KnowledgeSourceType;
    sourceFilename: string | null;
    sourceMimeType: string | null;
    sourceUrl: string | null;
    scope: KnowledgeScope;
    indexStatus: KnowledgeIndexStatus;
    indexErrorMessage: string | null;
    chunkCount: number;
    createdBy: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface KnowledgeChunkPreview {
    id: string;
    chunkIndex: number;
    text: string;
    charStart: number | null;
    charEnd: number | null;
}

export interface KnowledgeEntryDetail extends KnowledgeEntrySummary {
    rawContent: string | null;
    sampleChunks: KnowledgeChunkPreview[];
}

export interface KnowledgeListResponse {
    entries: KnowledgeEntrySummary[];
    total: number;
    page: number;
    limit: number;
}

export interface KnowledgeListFilters {
    page?: number;
    limit?: number;
    indexStatus?: KnowledgeIndexStatus;
    titleContains?: string;
}

export interface RankedKnowledgeChunk {
    chunkId: string;
    entryId: string;
    text: string;
    chunkIndex: number;
    charStart: number | null;
    charEnd: number | null;
    distance: number;
    entryTitle: string;
    entrySourceType: KnowledgeSourceType;
    entryScope: KnowledgeScope;
}

export interface KnowledgeSearchPreviewBody {
    query: string;
    professionKeys?: string[];
    languages?: string[];
    audience?: KnowledgeAudience;
    topK?: number;
}

export interface KnowledgeAnswerPreviewBody extends KnowledgeSearchPreviewBody {
    /** Language tag for the AI reply (default 'EN'). */
    language?: string;
}

export interface KnowledgeAnswerPreviewResponse {
    chunks: RankedKnowledgeChunk[];
    reply: string | null;
    usedChunks: number;
}

export const listKnowledgeEntries = async (
    filters: KnowledgeListFilters = {},
): Promise<KnowledgeListResponse> => {
    try {
        const params: Record<string, string | number> = {};
        if (filters.page) params.page = filters.page;
        if (filters.limit) params.limit = filters.limit;
        if (filters.indexStatus) params.indexStatus = filters.indexStatus;
        if (filters.titleContains) params.titleContains = filters.titleContains;
        const response = await axiosInstance.get<KnowledgeListResponse>(
            '/admin/knowledge/entries',
            { params },
        );
        return response.data;
    } catch (error: any) {
        logger.error('Failed to list knowledge entries', error);
        throw error;
    }
};

export const getKnowledgeEntry = async (id: string): Promise<KnowledgeEntryDetail> => {
    try {
        const response = await axiosInstance.get<KnowledgeEntryDetail>(
            `/admin/knowledge/entries/${id}`,
        );
        return response.data;
    } catch (error: any) {
        logger.error('Failed to get knowledge entry', error);
        throw error;
    }
};

export const createKnowledgeFromPaste = async (input: {
    title: string;
    text: string;
    scope?: KnowledgeScope;
}): Promise<KnowledgeEntrySummary> => {
    try {
        const response = await axiosInstance.post<KnowledgeEntrySummary>(
            '/admin/knowledge/paste',
            input,
        );
        return response.data;
    } catch (error: any) {
        logger.error('Failed to create knowledge entry from paste', error);
        throw error;
    }
};

export const createKnowledgeFromUrl = async (input: {
    title: string;
    url: string;
    scope?: KnowledgeScope;
}): Promise<KnowledgeEntrySummary> => {
    try {
        const response = await axiosInstance.post<KnowledgeEntrySummary>(
            '/admin/knowledge/url',
            input,
        );
        return response.data;
    } catch (error: any) {
        logger.error('Failed to create knowledge entry from URL', error);
        throw error;
    }
};

export const uploadKnowledgeFile = async (input: {
    title: string;
    file: File;
    scope?: KnowledgeScope;
}): Promise<KnowledgeEntrySummary> => {
    try {
        const form = new FormData();
        form.append('title', input.title);
        if (input.scope) form.append('scope', JSON.stringify(input.scope));
        form.append('file', input.file);
        const response = await axiosInstance.post<KnowledgeEntrySummary>(
            '/admin/knowledge/upload',
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
    } catch (error: any) {
        logger.error('Failed to upload knowledge file', error);
        throw error;
    }
};

export const deleteKnowledgeEntry = async (id: string): Promise<void> => {
    try {
        await axiosInstance.delete(`/admin/knowledge/entries/${id}`);
    } catch (error: any) {
        logger.error('Failed to delete knowledge entry', error);
        throw error;
    }
};

export const reindexKnowledgeEntry = async (id: string): Promise<KnowledgeEntrySummary> => {
    try {
        const response = await axiosInstance.post<KnowledgeEntrySummary>(
            `/admin/knowledge/entries/${id}/reindex`,
        );
        return response.data;
    } catch (error: any) {
        logger.error('Failed to reindex knowledge entry', error);
        throw error;
    }
};

export const searchKnowledgePreview = async (
    body: KnowledgeSearchPreviewBody,
): Promise<{ chunks: RankedKnowledgeChunk[] }> => {
    try {
        const response = await axiosInstance.post<{ chunks: RankedKnowledgeChunk[] }>(
            '/admin/knowledge/search-preview',
            body,
        );
        return response.data;
    } catch (error: any) {
        logger.error('Failed to run knowledge search preview', error);
        throw error;
    }
};

export const answerKnowledgePreview = async (
    body: KnowledgeAnswerPreviewBody,
): Promise<KnowledgeAnswerPreviewResponse> => {
    try {
        const response = await axiosInstance.post<KnowledgeAnswerPreviewResponse>(
            '/admin/knowledge/answer-preview',
            body,
        );
        return response.data;
    } catch (error: any) {
        logger.error('Failed to run knowledge answer preview', error);
        throw error;
    }
};

export const knowledgeApi = {
    list: listKnowledgeEntries,
    get: getKnowledgeEntry,
    createFromPaste: createKnowledgeFromPaste,
    createFromUrl: createKnowledgeFromUrl,
    uploadFile: uploadKnowledgeFile,
    delete: deleteKnowledgeEntry,
    reindex: reindexKnowledgeEntry,
    searchPreview: searchKnowledgePreview,
    answerPreview: answerKnowledgePreview,
};
